import React, { useState } from "react";
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  Paper,
  CircularProgress,
  Alert
} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import ApiService from "../../services/api-service";
import notifier from "../../components/Notifier";
import { format } from 'date-fns';

export default function Reports() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [usageIdentifier, setUsageIdentifier] = useState(1);
  const [loading, setLoading] = useState(false);

  const usageTypes = [
    { value: 1, label: "Music Streams" },
    { value: 2, label: "Video Plays" },
    { value: 3, label: "Downloads" },
    { value: 4, label: "All Usage" }
  ];

  const handleDownloadReport = async () => {
    if (!startDate || !endDate) {
      notifier.error("Please select both start and end dates");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      notifier.error("Start date cannot be after end date");
      return;
    }

    setLoading(true);
    try {
      // Dates are already in YYYY-MM-DD format from input type="date"
      const formattedStartDate = startDate;
      const formattedEndDate = endDate;

      console.log('Requesting MLC report with params:', {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        usageIdentifier: usageIdentifier
      });

      // Call the MLC report endpoint
      const response = await ApiService.GET_MLC_REPORT({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        usageIdentifier: usageIdentifier
      });

      // Create a blob from the CSV data
      const csvData = response.data || response;
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = url;
      link.download = `mlc_report_${formattedStartDate}_to_${formattedEndDate}.csv`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      notifier.success("Report downloaded successfully");
    } catch (error) {
      console.error("Error downloading report:", error);
      notifier.error("Failed to download report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ color: "#fff", mb: 4 }}>
        MLC Usage Reports
      </Typography>
      
      <Paper sx={{ p: 4, backgroundColor: "rgba(255,255,255,0.05)" }}>
        <Typography variant="h6" sx={{ color: "#fff", mb: 3 }}>
          Generate Usage Report
        </Typography>
        
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate || ''}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ 
                flex: 1, 
                minWidth: 200,
                '& .MuiInputBase-root': { color: '#fff' },
                '& .MuiInputLabel-root': { color: '#ccc' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                '& input': { color: '#fff' }
              }} 
            />
            
            <TextField
              label="End Date"
              type="date"
              value={endDate || ''}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ 
                flex: 1, 
                minWidth: 200,
                '& .MuiInputBase-root': { color: '#fff' },
                '& .MuiInputLabel-root': { color: '#ccc' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                '& input': { color: '#fff' }
              }} 
            />
              
              <FormControl sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel sx={{ color: '#ccc' }}>Usage Type</InputLabel>
                <Select
                  value={usageIdentifier}
                  onChange={(e) => setUsageIdentifier(e.target.value)}
                  label="Usage Type"
                  sx={{
                    color: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                    '& .MuiSvgIcon-root': { color: '#fff' }
                  }}
                >
                  {usageTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleDownloadReport}
                disabled={loading || !startDate || !endDate}
                startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                sx={{
                  backgroundColor: "#FFEE20",
                  color: "#000",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "#FFD700"
                  },
                  "&:disabled": {
                    backgroundColor: "#666",
                    color: "#999"
                  }
                }}
              >
                {loading ? "Generating Report..." : "Download Report"}
              </Button>
              
              {loading && (
                <Typography variant="body2" sx={{ color: "#ccc" }}>
                  This may take a few moments...
                </Typography>
              )}
            </Box>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              Reports include detailed usage data for the selected date range and type. 
              The downloaded file will be in CSV format for easy analysis.
            </Alert>
          </Box>
      </Paper>
    </Box>
  );
}